/**
 * Frontend Logging Utility
 * 
 * Provides conditional logging with different log levels.
 * Logs can be enabled/disabled via environment variables or localStorage.
 * 
 * Environment variables:
 * - VITE_LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error' | 'none' (default: 'warn' in production, 'debug' in development)
 * 
 * localStorage overrides (for debugging in production):
 * - citiguide:logLevel: same values as above
 * - citiguide:logModules: comma-separated list of modules to enable (e.g., 'WebSocket,MapPage')
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

interface LoggerConfig {
  level: LogLevel;
  enabledModules: string[] | null; // null means all modules enabled
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

function getConfig(): LoggerConfig {
  // Check localStorage first (allows runtime debugging)
  const storedLevel = typeof localStorage !== 'undefined' 
    ? localStorage.getItem('citiguide:logLevel') as LogLevel | null
    : null;
  
  const storedModules = typeof localStorage !== 'undefined'
    ? localStorage.getItem('citiguide:logModules')
    : null;

  // Determine log level
  let level: LogLevel;
  if (storedLevel && LOG_LEVELS[storedLevel] !== undefined) {
    level = storedLevel;
  } else if (import.meta.env.VITE_LOG_LEVEL) {
    level = import.meta.env.VITE_LOG_LEVEL as LogLevel;
  } else {
    // Default: debug in development, warn in production
    level = import.meta.env.DEV ? 'debug' : 'warn';
  }

  // Parse enabled modules
  const enabledModules = storedModules ? storedModules.split(',').map(m => m.trim()) : null;

  return { level, enabledModules };
}

function shouldLog(level: LogLevel, module?: string): boolean {
  const config = getConfig();
  
  // Check log level
  if (LOG_LEVELS[level] < LOG_LEVELS[config.level]) {
    return false;
  }

  // Check module filter (exact match only)
  if (config.enabledModules && module) {
    return config.enabledModules.includes(module);
  }

  return true;
}

function formatMessage(module: string | undefined, message: string): string {
  if (module) {
    return `[${module}] ${message}`;
  }
  return message;
}

/**
 * Create a logger instance for a specific module
 */
export function createLogger(module: string) {
  return {
    debug: (message: string, ...args: unknown[]) => {
      if (shouldLog('debug', module)) {
        console.debug(formatMessage(module, message), ...args);
      }
    },
    info: (message: string, ...args: unknown[]) => {
      if (shouldLog('info', module)) {
        console.info(formatMessage(module, message), ...args);
      }
    },
    warn: (message: string, ...args: unknown[]) => {
      if (shouldLog('warn', module)) {
        console.warn(formatMessage(module, message), ...args);
      }
    },
    error: (message: string, ...args: unknown[]) => {
      if (shouldLog('error', module)) {
        console.error(formatMessage(module, message), ...args);
      }
    },
  };
}

/**
 * Global logger for one-off logging without a module context
 */
export const logger = {
  debug: (message: string, ...args: unknown[]) => {
    if (shouldLog('debug')) {
      console.debug(message, ...args);
    }
  },
  info: (message: string, ...args: unknown[]) => {
    if (shouldLog('info')) {
      console.info(message, ...args);
    }
  },
  warn: (message: string, ...args: unknown[]) => {
    if (shouldLog('warn')) {
      console.warn(message, ...args);
    }
  },
  error: (message: string, ...args: unknown[]) => {
    if (shouldLog('error')) {
      console.error(message, ...args);
    }
  },
};

/**
 * Helper to enable logging in production for debugging
 * Call from browser console: window.enableLogs('debug', 'WebSocket,MapPage')
 */
if (typeof window !== 'undefined') {
  (window as unknown as { enableLogs: (level: LogLevel, modules?: string) => void }).enableLogs = (
    level: LogLevel,
    modules?: string
  ) => {
    localStorage.setItem('citiguide:logLevel', level);
    if (modules) {
      localStorage.setItem('citiguide:logModules', modules);
    } else {
      localStorage.removeItem('citiguide:logModules');
    }
    console.info(`Logging enabled: level=${level}, modules=${modules || 'all'}`);
    console.info('Reload the page for changes to take effect.');
  };

  (window as unknown as { disableLogs: () => void }).disableLogs = () => {
    localStorage.removeItem('citiguide:logLevel');
    localStorage.removeItem('citiguide:logModules');
    console.info('Logging reset to defaults. Reload the page for changes to take effect.');
  };
}

export default logger;
